import * as THREE from 'three';
import { AUTOSAVE_INTERVAL_MS, INCOME_TICK_MS, expansionCost } from '../config/balancing';
import { catalogItem, purchaseCost } from '../config/catalog';
import { FIRST_SESSION_DELAY_MS } from '../config/rewardedAds';
import { entityName, formatHudGoal, t } from '../i18n';
import { applyLandModeOverlay, mapParkStateToRender, worldCenterForSubcell } from '../migration/mapParkState';
import {
  acceptPlacementCommit,
  beginIdleMouseClickIntent,
  beginTouchIntent,
  cancelTouchIntent,
  cancelTouchIntentForPinch,
  createPinchGestureState,
  createPointerGateState,
  createTouchIntentState,
  hasPendingTouchIntent,
  isDesktopCameraPanPointer,
  isIdleCameraPanMode,
  isOneFingerCameraPanAllowed,
  isPinchActive,
  isPinchSuppressingSceneAction,
  isTouchScenePointer,
  notePinchPointerDown,
  notePinchPointerMove,
  notePinchPointerUp,
  pickSceneHitFromPointer,
  pickSubcellFromPointer,
  pickWorldOnParkPlane,
  PINCH_INTENT_WINDOW_MS,
  rememberAcceptedPointer,
  SCENE_POINTER_DEBOUNCE_MS,
  shouldAcceptScenePointer,
  shouldStartIdleOneFingerPan,
  takeFlushableTouchIntent,
  worldPanFromClientDelta,
  type SceneHit,
} from '../migration/pickSlot';
import { createInteractionPreviewRoot, syncInteractionPreview } from '../migration/placementPreviewVisual';
import {
  evaluateFloorBrushPreview,
  evaluatePlacementPreview,
  nextPreviewRotation,
  type FloorBrushPreview,
  type PlacementPreview,
} from '../sim/placementPreview';
import { recommendedPlotIdsForKind, purchasableFrontierPlotIds } from '../sim/landGuidance';
import { queryBuildableSubcell, compatibilitySlotIdForAnchor } from '../spatial/buildableArea';
import { landOwnsPlot, plotById, plotIdContainingWorld } from '../spatial/plots';
import { centerAnchorForSlotId, entityAnchor, entityRotation, slotByIdForAnchor } from '../sim/placement';
import type { EntityKind, ParkState, PlacedEntity, RotationQuarter, SubcellAnchor } from '../types';
import { bindProductionSessionWatch, startPlayableAnalytics } from '../migration/playableAnalytics';
import {
  createMigrationSceneHost,
  parkViewRotateDeltaFromKey,
  parkViewZoomDeltaFromKey,
  PRODUCTION_ZOOM_STEP,
  zoomDeltaFromWheel,
  type MigrationSceneHost,
} from '../migration/sceneHost';
import { applyGuideMarker, ensureGuideMarker, guideMarkerAnchor, type GuideRect } from '../onboarding/guideMarker';
import {
  firstUnlockedEmptySlotId,
  guidedCatalogKind,
  primaryGuideTarget,
  recommendedGuideAnchor,
  resolveOnboardingStep,
  type PrimaryGuide,
} from '../onboarding/onboarding';
import { collectCueEligible, collectCueHeight, ensureCollectCueLayer, projectWorldToOverlay } from './collectCues';
import { SAVE_STORAGE_KEY } from '../persistence/localStorage';
import { CrazyGamesDataAccessError, selectCanonicalPersistence } from '../persistence/crazyGamesData';
import { runtime } from '../runtime';
import { resolveApprovedPaletteId } from '../sim/appearance';
import { CAROUSEL_PALETTES, COASTER_PALETTES } from '../spikes/threejs/palettes';
import { createHud, failMessage, guidedConfirmControlVisible, guidedConfirmPlan } from '../ui/hud';
import { openCanonicalThreeJsSession } from './canonicalCutover';
import { playSuccessfulActionCue } from '../audio/actionFeedback';
import { createAmbientChannel, maybePlayAttractionAccent, nudgeAmbientResume } from '../audio/ambient';
import { createMusicChannel, nudgeMusicResume } from '../audio/music';
import {
  isEffectivelyMuted,
  isSoundMuted,
  setSoundMuted,
  subscribeAudioSettings,
} from '../audio/soundSettings';
import {
  bindCrazyGamesPlatformAudio,
  noteCrazyGamesAudioUnavailable,
  setAdBreakPlatformMuted,
} from '../platform/crazyGamesAudio';
import { emitGameplayStart, getInitializedCrazyGamesSdk, initCrazyGamesSdk } from '../platform/crazyGamesSdk';
import {
  autoCollectRemainingMs,
  canShowBoostChrome,
  hasAttractionBuildCoupon,
  isAutoCollectActive,
  isRewardedEligible,
  isRewardedSurfaceIdle,
  landDiscountParcelId,
  landEffectiveCost,
  requestRewardedAd,
  rewardedCooldownRemainingMs,
  setRewardedAdHooks,
  shouldSurfacePendingOfflineOffer,
  type RewardedKind,
} from '../platform/rewardedAds';
import { resolveProductionSlotOpen } from './explicitCollect';
import { interactionSceneSync, type SceneSyncKind } from './sceneSync';

function availablePalettes(entity: PlacedEntity): Array<{ id: string; label: string }> {
  if (entity.kind === 'starter_ride') {
    return Object.values(CAROUSEL_PALETTES).map((preset) => ({ id: preset.id, label: preset.label }));
  }
  if (entity.kind === 'roller_coaster') {
    return Object.values(COASTER_PALETTES).map((preset) => ({ id: preset.id, label: preset.label }));
  }
  return [];
}

function showIntegrityFailure(): void {
  const node = document.getElementById('save-integrity');
  if (node) {
    node.hidden = false;
  }
}

/** COL-003C: drop the removed slot's selection ring by clearing it before scene rebuild. */
export function clearSelectedSlotForRebuild(session: { selectedSlotId: string | null }): void {
  session.selectedSlotId = null;
}

export type LiveSelectionIntent = 'entity' | 'expand' | 'none';

/** Keep the orange ring only while an entity or locked-land panel is intentionally open. */
export function resolveLiveSelectionIntent(args: {
  selectedSlotId: string | null;
  inspectedEntityId: string | null;
  state: ParkState;
}): LiveSelectionIntent {
  if (args.inspectedEntityId && args.state.entities.some((entity) => entity.id === args.inspectedEntityId)) {
    return 'entity';
  }
  if (args.selectedSlotId) {
    const opened = resolveProductionSlotOpen(args.state, args.selectedSlotId);
    if (opened.kind === 'expand') {
      return 'expand';
    }
  }
  return 'none';
}

export function selectedSlotIdAfterInteractionExit(
  selectedSlotId: string | null,
  liveIntent: LiveSelectionIntent,
): string | null {
  return liveIntent === 'none' ? null : selectedSlotId;
}

/** Geometry-picked entity wins over legacy slot occupancy (entityAtSlot). */
export function resolveProductionSceneHit(
  state: ParkState,
  hit: SceneHit,
): { kind: 'entity'; entity: PlacedEntity } | { kind: 'slot'; slotId: string } {
  if (hit.entityId) {
    const entity = state.entities.find((item) => item.id === hit.entityId);
    if (entity) {
      return { kind: 'entity', entity };
    }
  }
  return { kind: 'slot', slotId: hit.slotId };
}

/** Free unlocked canvas seeds Build at the exact subcell; slot occupancy never reopens an occupant. */
export function resolveUnlockedGroundBuildSeed(
  state: ParkState,
  coord: SubcellAnchor | null,
):
  | { kind: 'build'; slotId: string; anchor: SubcellAnchor }
  | { kind: 'expand'; slotId: string; landId: string }
  | { kind: 'locked' }
  | { kind: 'none' } {
  if (!coord) {
    return { kind: 'none' };
  }
  const query = queryBuildableSubcell(coord, state.unlockedLandIds);
  if (!query.insideCanvas) {
    return { kind: 'locked' };
  }
  const slotId = compatibilitySlotIdForAnchor(coord);
  if (!query.unlocked) {
    return { kind: 'expand', slotId, landId: query.plotId ?? slotId };
  }
  return { kind: 'build', slotId, anchor: { sx: coord.sx, sz: coord.sz } };
}

export type LockedGroundIntent = { kind: 'buy'; landId: string } | { kind: 'toast-use-land' } | { kind: 'ignore' };

/** Build/Floor/idle never open land purchase. Land mode buys any known locked plot. */
export function resolveLockedGroundIntent(args: {
  landMode: boolean;
  frontierPlotIds: readonly string[];
  landId: string;
  unlockedLandIds?: readonly string[];
}): LockedGroundIntent {
  if (!args.landMode) {
    return { kind: 'toast-use-land' };
  }
  if (args.unlockedLandIds) {
    if (!plotById(args.landId) || landOwnsPlot(args.unlockedLandIds, args.landId)) {
      return { kind: 'ignore' };
    }
    return { kind: 'buy', landId: args.landId };
  }
  if (args.frontierPlotIds.includes(args.landId)) {
    return { kind: 'buy', landId: args.landId };
  }
  return { kind: 'ignore' };
}

/**
 * Land-mode ground pick: the visible outer overlay rectangle, not only the
 * inset canvas cell, is a purchase target. Idle/Build/Floor never buy.
 */
export type PanelCloseOwner = 'placement' | 'boosts' | 'build' | 'land' | 'floor' | 'inspect';

/** X on a mode-owned panel exits that mode. Informational inspection only closes. */
export function resolvePanelCloseOwner(args: {
  placementActive: boolean;
  buildMode: boolean;
  landMode: boolean;
  selectedLandId: string | null;
  landGuidanceActive: boolean;
  floorMode: boolean;
  boostsOpen?: boolean;
}): PanelCloseOwner {
  if (args.placementActive) {
    return 'placement';
  }
  if (args.boostsOpen) {
    return 'boosts';
  }
  if (args.buildMode) {
    return 'build';
  }
  if (args.landMode || args.selectedLandId != null || args.landGuidanceActive) {
    return 'land';
  }
  if (args.floorMode) {
    return 'floor';
  }
  return 'inspect';
}

export function resolveLandModePurchaseFromWorld(args: {
  landMode: boolean;
  x: number;
  z: number;
  frontierPlotIds: readonly string[];
  unlockedLandIds: readonly string[];
}): LockedGroundIntent | { kind: 'none' } {
  const plotId = plotIdContainingWorld(args.x, args.z);
  if (!plotId) {
    return { kind: 'none' };
  }
  if (!args.landMode) {
    return landOwnsPlot(args.unlockedLandIds, plotId) ? { kind: 'none' } : { kind: 'toast-use-land' };
  }
  return resolveLockedGroundIntent({
    landMode: true,
    frontierPlotIds: args.frontierPlotIds,
    landId: plotId,
    unlockedLandIds: args.unlockedLandIds,
  });
}

export function startProductionPark(): void {
  const mount = document.getElementById('game');
  if (!mount) {
    throw new Error('Missing #game');
  }
  ensureCollectCueLayer();
  void bootProductionPark(mount);
}

async function bootProductionPark(mount: HTMLElement): Promise<void> {
  const status = await initCrazyGamesSdk();
  const sdk = status.usable ? getInitializedCrazyGamesSdk() : null;
  const selected = selectCanonicalPersistence(status, sdk);
  if (!selected.ok) {
    showIntegrityFailure();
    return;
  }
  if (selected.backend === 'data-module') {
    bindCrazyGamesPlatformAudio(selected.sdk);
  } else {
    noteCrazyGamesAudioUnavailable();
  }

  const opened = (() => {
    try {
      return openCanonicalThreeJsSession(selected.persistence, selected.backup);
    } catch (error) {
      if (error instanceof CrazyGamesDataAccessError) {
        return null;
      }
      throw error;
    }
  })();
  if (!opened || !opened.ok) {
    showIntegrityFailure();
    return;
  }

  const session = opened.session;
  runtime.session = session;
  const analytics = startPlayableAnalytics();
  const sessionWatch = bindProductionSessionWatch(analytics, window);
  let webglLoopStarted = false;

  let pendingRemove: { entityId: string; slotId: string } | null = null;
  let inspectedEntityId: string | null = null;
  let selectedLandId: string | null = null;
  let landGuidanceKind: EntityKind | null = null;
  let floorMode = false;
  let landMode = false;
  let paintPointerId: number | null = null;
  let lastPaintKey: string | null = null;
  let host: MigrationSceneHost | null = null;
  let previewRoot: THREE.Group | null = null;
  let placementDraft: {
    mode: 'move' | 'build';
    kind: EntityKind;
    entityId?: string;
    rotation: RotationQuarter;
    lastAnchor: SubcellAnchor;
  } | null = null;
  let latestPlacementPreview: PlacementPreview | null = null;
  let latestFloorBrush: FloorBrushPreview | null = null;
  let pendingBuildAnchor: SubcellAnchor | null = null;
  let previewPointerId: number | null = null;
  let tickAcc = 0;
  let saveAcc = 0;
  let floorRebuildRaf = 0;
  let lastAnnouncedOnboarding: ReturnType<typeof resolveOnboardingStep> | null = null;
  let boostsOpen = false;
  let welcomeBackOpen = false;
  let welcomeBackAmount = 0;
  let pendingOfflineOffer = false;
  let worldInteractionActive = false;
  const pointerGate = createPointerGateState();
  const pinchGesture = createPinchGestureState();
  const touchIntent = createTouchIntentState();
  const ambient = createAmbientChannel();
  const music = createMusicChannel();

  const hud = createHud({
    onToggleBuild: () => {
      cancelPlacementDraft('toggle');
      pendingBuildAnchor = null;
      session.buildMode = !session.buildMode;
      if (session.buildMode) {
        floorMode = false;
        exitLandMode(false);
        hud.setFloorMode(false);
        clearFloorBrush();
        openBuildCatalog(null, true);
      } else {
        hud.hidePanel();
        clearSelectionIfNoLiveEntityOrLand();
      }
      hud.setBuildMode(session.buildMode);
      applySubgridCue();
      refreshHud();
    },
    onToggleLand: () => {
      if (landMode) {
        exitLandMode(true);
        return;
      }
      enterLandMode();
    },
    onFindLand: (kind) => {
      enterLandMode(kind);
    },
    onToggleFloor: () => {
      cancelPlacementDraft('toggle');
      pendingBuildAnchor = null;
      floorMode = !floorMode;
      if (floorMode) {
        session.buildMode = false;
        exitLandMode(false);
        hud.setBuildMode(false);
        hud.hidePanel();
        clearIdleSelectionRing();
      } else {
        clearFloorBrush();
        clearIdleSelectionRing();
      }
      hud.setFloorMode(floorMode);
      applySubgridCue();
      syncBoostChrome();
    },
    onToggleBoost: () => {
      if (!canShowRewardedUi() || worldInteractionActive || isPinchActive(pinchGesture)) {
        return;
      }
      openBoostsPanel();
    },
    onGuidedConfirm: () => {
      tryGuidedConfirm();
    },
    onCollectAll: () => {
      tryCollectAll();
    },
    onClosePanel: () => {
      if (welcomeBackOpen) {
        session.closeOfflineOffer();
        welcomeBackOpen = false;
        pendingOfflineOffer = false;
        hud.hidePanel();
        syncBoostChrome();
        return;
      }
      const owner = resolvePanelCloseOwner({
        placementActive: placementDraft != null,
        buildMode: session.buildMode,
        landMode,
        selectedLandId,
        landGuidanceActive: landGuidanceKind != null,
        floorMode,
        boostsOpen,
      });
      if (owner === 'boosts') {
        boostsOpen = false;
        hud.hidePanel();
        syncBoostChrome();
        return;
      }
      if (owner === 'placement' || owner === 'build') {
        cancelPlacementDraft('close');
        pendingBuildAnchor = null;
        session.buildMode = false;
        hud.setBuildMode(false);
        clearIdleSelectionRing();
        applySubgridCue();
        refreshHud();
        return;
      }
      if (owner === 'land') {
        exitLandMode(true);
        return;
      }
      if (owner === 'floor') {
        floorMode = false;
        clearFloorBrush();
        hud.setFloorMode(false);
        clearIdleSelectionRing();
        applySubgridCue();
        return;
      }
      selectedLandId = null;
      clearIdleSelectionRing();
      syncBoostChrome();
    },
    onToggleSound: () => {
      setSoundMuted(!isSoundMuted());
      ambient.setMuted(isEffectivelyMuted());
      music.setMuted(isEffectivelyMuted());
      hud.setSoundMuted(isSoundMuted());
      refreshScene(interactionSceneSync({ type: 'sound' }));
    },
    onLocaleChange: () => {
      refreshScene(interactionSceneSync({ type: 'language' }));
      if (welcomeBackOpen) {
        openWelcomeBackPanel();
        return;
      }
      if (boostsOpen) {
        openBoostsPanel();
        return;
      }
      if (placementDraft?.mode === 'build') {
        showBuildDraftPanel(placementDraft.kind);
        return;
      }
      if (landMode && selectedLandId) {
        openLandPurchase(selectedLandId);
        return;
      }
      if (inspectedEntityId) {
        showEntityPanel(inspectedEntityId);
        return;
      }
      maybeSurfacePendingOfflineOffer();
    },
    onReset: () => {
      session.resetPark();
      window.location.reload();
    },
    onRotateView: (delta) => {
      host?.rotateView(delta);
      syncCollectCues();
    },
    onZoomView: (delta) => {
      host?.zoomBy(delta * PRODUCTION_ZOOM_STEP);
      syncCollectCues();
    },
  });
  hud.setSoundMuted(isSoundMuted());
  ambient.setMuted(isEffectivelyMuted());
  music.setMuted(isEffectivelyMuted());
  runtime.hud = hud;
  subscribeAudioSettings(() => {
    ambient.setMuted(isEffectivelyMuted());
    music.setMuted(isEffectivelyMuted());
    hud.setSoundMuted(isSoundMuted());
  });

  function nowMs(): number {
    return Date.now();
  }

  function canShowRewardedUi(): boolean {
    return isRewardedEligible(session.state, nowMs(), session.startedAt);
  }

  function isUiIdleForOfflineOffer(): boolean {
    return isRewardedSurfaceIdle({
      buildMode: session.buildMode,
      landMode,
      floorMode,
      placementActive: placementDraft != null,
      worldInteractionActive,
      pinchActive: isPinchActive(pinchGesture),
      boostsOpen,
      inspectingEntity: inspectedEntityId != null,
      inspectingLand: selectedLandId != null,
    });
  }

  function maybeSurfacePendingOfflineOffer(): void {
    if (
      !shouldSurfacePendingOfflineOffer({
        pending: pendingOfflineOffer,
        offerClosed: session.offlineOfferClosed,
        alreadyOpen: welcomeBackOpen,
        eligible: canShowRewardedUi(),
        idle: isUiIdleForOfflineOffer(),
      })
    ) {
      return;
    }
    pendingOfflineOffer = false;
    openWelcomeBackPanel();
  }

  function currentRewardedCta(onWatch: () => void): {
    visible: boolean;
    cooldownMs: number;
    onWatch: () => void;
  } {
    const cooldownMs = rewardedCooldownRemainingMs(nowMs());
    return {
      visible: canShowRewardedUi(),
      cooldownMs,
      onWatch,
    };
  }

  function syncBoostChrome(): void {
    const show = canShowBoostChrome({
      eligible: canShowRewardedUi(),
      worldInteractionActive,
      pinchActive: isPinchActive(pinchGesture),
      placementActive: placementDraft != null,
      floorMode,
    });
    hud.setBoostVisible(show);
    maybeSurfacePendingOfflineOffer();
  }

  function openBoostsPanel(): void {
    if (!canShowRewardedUi()) {
      hud.setBoostVisible(false);
      return;
    }
    boostsOpen = true;
    welcomeBackOpen = false;
    const remaining = autoCollectRemainingMs(nowMs());
    const minutes = Math.ceil(remaining / 60000);
    const seconds = Math.ceil(remaining / 1000);
    hud.showBoosts({
      autoCollectActive: isAutoCollectActive(nowMs()),
      remainingLabel: remaining > 0
        ? t('rewarded.autoCollectActive', { n: remaining >= 60000 ? `${minutes}m` : `${seconds}s` })
        : undefined,
      rewarded: currentRewardedCta(() => void requestPlacement('auto_collect')),
    });
  }

  function openWelcomeBackPanel(amount?: number): void {
    if (amount !== undefined) {
      welcomeBackAmount = amount;
    }
    welcomeBackOpen = true;
    boostsOpen = false;
    hud.showWelcomeBack(welcomeBackAmount, {
      onContinue: () => {
        session.closeOfflineOffer();
        welcomeBackOpen = false;
        pendingOfflineOffer = false;
        hud.hidePanel();
        syncBoostChrome();
      },
      rewarded: currentRewardedCta(() => void requestPlacement('offline_double')),
    });
  }

  async function requestPlacement(kind: RewardedKind, parcelId?: string): Promise<void> {
    if (worldInteractionActive || isPinchActive(pinchGesture) || placementDraft || floorMode) {
      return;
    }
    const result = await requestRewardedAd(kind, {
      now: nowMs(),
      clock: nowMs,
      state: session.state,
      sessionStartedAt: session.startedAt,
      parcelId,
      grantOfflineExtra: kind === 'offline_double' ? () => {
        session.grantOfflineDoubleBonus();
      } : undefined,
    });
    if (kind === 'offline_double' && result.ok) {
      welcomeBackOpen = false;
      pendingOfflineOffer = false;
      hud.hidePanel();
    }
    if (kind === 'auto_collect' && boostsOpen) {
      openBoostsPanel();
    } else if (kind === 'build_discount' && session.buildMode) {
      openBuildCatalog(session.selectedSlotId, true);
    } else if (kind === 'land_discount' && selectedLandId) {
      openLandPurchase(selectedLandId);
    }
    refreshHud();
  }

  setRewardedAdHooks({
    setOverlay: (active) => hud.setAdBreakActive(active),
    setPlatformMuted: setAdBreakPlatformMuted,
    onUnavailable: () => hud.toast(t('rewarded.unavailable')),
  });

  function currentFrontierIds(): string[] {
    return purchasableFrontierPlotIds(session.state.unlockedLandIds);
  }

  function currentRecommendedIds(): string[] {
    if (!landGuidanceKind) {
      return [];
    }
    return recommendedPlotIdsForKind(landGuidanceKind, session.state.unlockedLandIds);
  }

  function renderModel() {
    const recommendedPlotIds = currentRecommendedIds();
    return applyLandModeOverlay(
      {
        ...mapParkStateToRender(session.state),
        selectedSlotId: session.selectedSlotId,
        buildMode: session.buildMode || floorMode || Boolean(placementDraft),
      },
      {
        landMode,
        visiblePlotIds: landMode ? [...new Set([...currentFrontierIds(), ...recommendedPlotIds])] : [],
        recommendedPlotIds,
      },
    );
  }

  function enterLandMode(guidanceKind?: EntityKind): void {
    cancelPlacementDraft('toggle');
    pendingBuildAnchor = null;
    session.buildMode = false;
    floorMode = false;
    landMode = true;
    landGuidanceKind = guidanceKind ?? null;
    selectedLandId = null;
    inspectedEntityId = null;
    pendingRemove = null;
    hud.setBuildMode(false);
    hud.setFloorMode(false);
    hud.setLandMode(true);
    clearFloorBrush();
    hud.hidePanel();
    session.selectedSlotId = null;
    applySubgridCue();
    refreshScene(interactionSceneSync({ type: 'toggleLand' }));
    if (guidanceKind) {
      hud.toast(t('toast.findLandAdvance', { name: entityName(guidanceKind) }));
    }
  }

  function exitLandMode(fullExit: boolean): void {
    const wasLand = landMode || selectedLandId != null || landGuidanceKind != null;
    landMode = false;
    landGuidanceKind = null;
    selectedLandId = null;
    hud.setLandMode(false);
    if (!wasLand) {
      return;
    }
    if (fullExit) {
      inspectedEntityId = null;
    }
    session.selectedSlotId = null;
    hud.hidePanel();
    applySubgridCue();
    refreshScene(interactionSceneSync({ type: 'toggleLand' }));
    syncBoostChrome();
  }

  function projectWorldToHud(world: { x: number; z: number } | null): { x: number; y: number } | null {
    if (!host || !world) {
      return null;
    }
    const point = new THREE.Vector3(world.x, host.parkTop, world.z).project(host.camera);
    const rect = host.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    return {
      x: ((point.x + 1) / 2) * rect.width,
      y: ((1 - point.y) / 2) * rect.height,
    };
  }

  function activeTargetWorld(): { x: number; z: number } | null {
    if (placementDraft) {
      return worldCenterForSubcell(placementDraft.lastAnchor);
    }
    if (host) {
      if (inspectedEntityId) {
        const entity = host.model.entities.find((item) => item.id === inspectedEntityId);
        if (entity) {
          return { x: entity.x, z: entity.z };
        }
      }
      if (selectedLandId) {
        const land = host.model.lands.find((item) => item.parcelId === selectedLandId);
        if (land) {
          return { x: land.x, z: land.z };
        }
      }
      if (session.selectedSlotId) {
        const slot = host.model.slots.find((item) => item.slotId === session.selectedSlotId);
        if (slot) {
          return { x: slot.x, z: slot.z };
        }
      }
    }
    return null;
  }

  function bindPanelToActiveTarget(): void {
    hud.setPanelTarget(projectWorldToHud(activeTargetWorld()));
  }

  function openLandPurchase(landId: string): void {
    selectedLandId = landId;
    inspectedEntityId = null;
    session.selectedSlotId = null;
    const voucherId = landDiscountParcelId();
    const effectiveCost = landEffectiveCost(expansionCost(landId), landId);
    hud.showExpand(session.state, landId, () => buyExpansion(landId), {
      recommendedPlotIds: currentRecommendedIds(),
      effectiveCost,
      rewarded: voucherId === landId
        ? { visible: false }
        : currentRewardedCta(() => void requestPlacement('land_discount', landId)),
    });
    refreshScene(interactionSceneSync({ type: 'selectLand' }));
    bindPanelToActiveTarget();
  }

  function applySelectionExit(liveIntent: LiveSelectionIntent): void {
    session.selectedSlotId = selectedSlotIdAfterInteractionExit(session.selectedSlotId, liveIntent);
    if (liveIntent === 'none') {
      inspectedEntityId = null;
      hud.hidePanel();
    }
    refreshScene(interactionSceneSync({ type: 'closePanel' }));
    syncBoostChrome();
  }

  /** Floor Done/toggle and panel close: no live entity/land interaction remains. */
  function clearIdleSelectionRing(): void {
    applySelectionExit('none');
  }

  /** Build Done: keep the ring only if an entity or locked-land panel is still open. */
  function clearSelectionIfNoLiveEntityOrLand(): void {
    applySelectionExit(
      resolveLiveSelectionIntent({
        selectedSlotId: session.selectedSlotId,
        inspectedEntityId,
        state: session.state,
      }),
    );
  }

  function applySubgridCue(): void {
    if (!host) {
      return;
    }
    const mode = session.buildMode || floorMode || placementDraft ? 'build' : 'normal';
    host.scene.traverse((object) => {
      if (typeof object.userData.setSubgridCueMode === 'function') {
        object.userData.setSubgridCueMode(mode);
      }
    });
  }

  function currentPrimaryGuide(): PrimaryGuide {
    const panel = document.getElementById('panel');
    const catalogOpen = Boolean(panel && !panel.hidden && panel.dataset.kind === 'build');
    const placingKind = placementDraft?.mode === 'build' ? placementDraft.kind : null;
    return primaryGuideTarget({
      state: session.state,
      flags: session.onboarding,
      catalogOpen,
      placingKind,
    });
  }

  function rectInLayer(layer: HTMLElement, element: HTMLElement): GuideRect {
    const layerRect = layer.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    return {
      left: rect.left - layerRect.left,
      top: rect.top - layerRect.top,
      right: rect.right - layerRect.left,
      bottom: rect.bottom - layerRect.top,
    };
  }

  function syncGuideMarker(guide: PrimaryGuide): void {
    const marker = ensureGuideMarker(document);
    const layer = marker.parentElement;
    if (!layer) {
      return;
    }
    const layerRect = layer.getBoundingClientRect();
    const viewport = { width: layerRect.width, height: layerRect.height };
    if (viewport.width <= 0 || viewport.height <= 0) {
      applyGuideMarker(marker, null);
      return;
    }
    const confirm = document.getElementById('btn-guided-confirm');
    if (confirm && !confirm.hidden && guidedConfirmControlVisible(session.state)) {
      applyGuideMarker(marker, guideMarkerAnchor(rectInLayer(layer, confirm), viewport, 'hud'));
      return;
    }
    if (guide.target === 'build_button') {
      const button = document.getElementById('btn-build');
      applyGuideMarker(marker, button ? guideMarkerAnchor(rectInLayer(layer, button), viewport, 'hud') : null);
      return;
    }
    if (guide.target === 'catalog') {
      const card = document.querySelector<HTMLElement>(
        `#panel-body .choice.guide[data-kind="${CSS.escape(guide.kind)}"]`,
      );
      applyGuideMarker(marker, card ? guideMarkerAnchor(rectInLayer(layer, card), viewport, 'hud') : null);
      return;
    }
    if (guide.target === 'collect') {
      const button = document.querySelector<HTMLElement>(
        `#collect-cues .collect-cue.guide[data-entity-id="${CSS.escape(guide.entityId)}"]`,
      );
      if (!button || button.hidden) {
        applyGuideMarker(marker, null);
        return;
      }
      applyGuideMarker(marker, guideMarkerAnchor(rectInLayer(layer, button), viewport, 'world'));
      return;
    }
    if (guide.target === 'placement' && host) {
      const anchor = recommendedGuideAnchor(session.state, guide.kind);
      if (!anchor) {
        applyGuideMarker(marker, null);
        return;
      }
      const point = worldCenterForSubcell(anchor);
      const projected = projectWorldToOverlay(
        host.camera,
        host.canvas,
        layer,
        new THREE.Vector3(point.x, host.parkTop + 0.35, point.z),
      );
      if (!projected.visible) {
        applyGuideMarker(marker, null);
        return;
      }
      applyGuideMarker(
        marker,
        guideMarkerAnchor(
          { left: projected.x, top: projected.y, right: projected.x, bottom: projected.y },
          viewport,
          'world',
        ),
      );
      return;
    }
    applyGuideMarker(marker, null);
  }

  function refreshHud(): void {
    const step = resolveOnboardingStep(session.state, session.onboarding);
    const previous = lastAnnouncedOnboarding;
    hud.refresh(session.state, step);
    if (previous !== step && step !== 'complete') {
      hud.toast(formatHudGoal(session.state, step));
    }
    lastAnnouncedOnboarding = step;
    syncCollectCues();
    syncBoostChrome();
  }

  function openBuildCatalog(slotId: string | null, canvas: boolean): void {
    const resolved =
      slotId ??
      firstUnlockedEmptySlotId(session.state, session.selectedSlotId) ??
      session.selectedSlotId ??
      'slot_a1';
    session.selectedSlotId = resolved;
    inspectedEntityId = null;
    pendingRemove = null;
    const kind = guidedCatalogKind(resolveOnboardingStep(session.state, session.onboarding));
    hud.showBuildChoices(
      resolved,
      session.state,
      (kind) => beginBuildDraft(kind, resolved),
      kind ?? undefined,
      canvas,
      {
        ...currentRewardedCta(() => void requestPlacement('build_discount')),
        couponActive: hasAttractionBuildCoupon(),
      },
    );
    bindPanelToActiveTarget();
    syncCollectCues();
  }

  function syncCollectCues(): void {
    const guide = currentPrimaryGuide();
    const panel = document.getElementById('panel');
    const panelOpen = Boolean(panel && !panel.hidden);
    hud.setBuildGuide(guide.target === 'build_button' && !guidedConfirmControlVisible(session.state, panelOpen));
    const layer = ensureCollectCueLayer();
    if (!host) {
      layer.replaceChildren();
      syncGuideMarker(guide);
      return;
    }
    const model = renderModel();
    const live = session.state.entities.filter(collectCueEligible);
    const seen = new Set<string>();
    for (const entity of live) {
      seen.add(entity.id);
      const spec = model.entities.find((item) => item.id === entity.id);
      if (!spec) {
        continue;
      }
      const world = new THREE.Vector3(spec.x, host.parkTop + collectCueHeight(entity.kind), spec.z);
      const pos = projectWorldToOverlay(host.camera, host.canvas, layer, world);
      let button = layer.querySelector<HTMLButtonElement>(`[data-entity-id="${CSS.escape(entity.id)}"]`);
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'collect-cue';
        button.dataset.entityId = entity.id;
        button.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          hud.suppressTransientPointer();
        });
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const id = button!.dataset.entityId;
          const target = session.state.entities.find((item) => item.id === id);
          if (!target || !collectCueEligible(target)) {
            refreshHud();
            return;
          }
          tryCollect(target);
          if (inspectedEntityId === target.id) {
            const open = session.state.entities.find((item) => item.id === target.id);
            if (open) {
              hud.refreshEntityIncome(open);
            }
          }
          refreshHud();
        });
        layer.appendChild(button);
      }
      const amount = Math.floor(entity.storedCoins);
      button.textContent = String(amount);
      button.hidden = !pos.visible;
      button.classList.toggle('guide', guide.target === 'collect' && guide.entityId === entity.id);
      button.style.left = `${pos.x}px`;
      button.style.top = `${pos.y}px`;
      button.setAttribute('aria-label', t('action.collectN', { n: amount }));
    }
    for (const node of [...layer.querySelectorAll<HTMLButtonElement>('.collect-cue')]) {
      if (!seen.has(node.dataset.entityId ?? '')) {
        node.remove();
      }
    }
    syncGuideMarker(guide);
  }

  function refreshScene(rebuild: boolean | SceneSyncKind): void {
    refreshHud();
    if (!host) {
      return;
    }
    const model = renderModel();
    const kind: SceneSyncKind = rebuild === true ? 'full' : rebuild === false ? 'palette' : rebuild;
    if (kind === 'full') {
      host.rebuild(model);
    } else if (kind === 'entities') {
      host.syncEntities(model);
    } else if (kind === 'floors') {
      host.syncFloors(model);
    } else if (kind === 'selection') {
      host.syncSelection(model);
    } else if (kind === 'land') {
      host.syncLandOverlays(model);
    } else if (kind === 'palette') {
      host.applyApprovedPalettes(model);
    }
    if (previewRoot && kind !== 'none') {
      syncInteractionPreview(previewRoot, latestPlacementPreview ?? latestFloorBrush, host.parkTop);
    }
  }

  function setEntityVisualHidden(entityId: string, hidden: boolean): void {
    if (!host) {
      return;
    }
    host.scene.traverse((object) => {
      const migration = object.userData.migration as { entityId?: string; visual?: string } | undefined;
      if (migration?.entityId === entityId && migration.visual !== 'queue') {
        object.visible = !hidden;
      }
    });
  }

  function clearFloorBrush(): void {
    latestFloorBrush = null;
    if (previewRoot && host) {
      syncInteractionPreview(previewRoot, latestPlacementPreview, host.parkTop);
    }
  }

  function clearPlacementVisual(): void {
    latestPlacementPreview = null;
    if (previewRoot && host) {
      syncInteractionPreview(previewRoot, latestFloorBrush, host.parkTop);
    }
  }

  function applyPlacementPreview(anchor: SubcellAnchor): PlacementPreview | null {
    if (!placementDraft) {
      return null;
    }
    placementDraft.lastAnchor = anchor;
    const preview = evaluatePlacementPreview(session.state, {
      mode: placementDraft.mode,
      kind: placementDraft.kind,
      entityId: placementDraft.entityId,
      anchor,
      rotation: placementDraft.rotation,
    });
    latestPlacementPreview = preview;
    if (previewRoot && host) {
      syncInteractionPreview(previewRoot, preview, host.parkTop);
    }
    return preview;
  }

  function onPreviewPointerMove(event: PointerEvent): void {
    if (!host || !placementDraft) {
      return;
    }
    const anchor = pickSubcellFromPointer(host.camera, event, host.canvas, host.parkTop);
    if (!anchor) {
      return;
    }
    applyPlacementPreview(anchor);
    bindPanelToActiveTarget();
  }

  function showMoveDraftPanel(entityId: string): void {
    showEntityPanel(entityId);
  }

  function showBuildDraftPanel(kind: EntityKind): void {
    hud.showPlacementDraft(t('title.place', { name: entityName(kind) }), {
      onRotate: () => rotatePlacementDraft(),
      onCancel: () => cancelPlacementDraft('cancel'),
    });
    bindPanelToActiveTarget();
  }

  function beginMoveDraft(entity: { id: string; kind: EntityKind; slotId: string }): void {
    const live = session.state.entities.find((item) => item.id === entity.id);
    if (!live) {
      return;
    }
    floorMode = false;
    hud.setFloorMode(false);
    clearFloorBrush();
    placementDraft = {
      mode: 'move',
      kind: live.kind,
      entityId: live.id,
      rotation: entityRotation(live),
      lastAnchor: entityAnchor(live),
    };
    syncBoostChrome();
    setEntityVisualHidden(live.id, true);
    applyPlacementPreview(entityAnchor(live));
    hud.toast(t('toast.dragRotate'));
    showMoveDraftPanel(live.id);
  }

  function beginBuildDraft(kind: EntityKind, slotId: string): void {
    floorMode = false;
    hud.setFloorMode(false);
    clearFloorBrush();
    placementDraft = {
      mode: 'build',
      kind,
      rotation: 0,
      lastAnchor: pendingBuildAnchor ?? centerAnchorForSlotId(slotId),
    };
    syncBoostChrome();
    pendingBuildAnchor = null;
    applyPlacementPreview(placementDraft.lastAnchor);
    hud.toast(t('toast.dragCharge'));
    showBuildDraftPanel(kind);
    syncCollectCues();
  }

  function rotatePlacementDraft(): void {
    if (!placementDraft) {
      return;
    }
    placementDraft.rotation = nextPreviewRotation(placementDraft.rotation);
    applyPlacementPreview(placementDraft.lastAnchor);
    if (placementDraft.mode === 'move' && placementDraft.entityId) {
      showMoveDraftPanel(placementDraft.entityId);
    } else {
      showBuildDraftPanel(placementDraft.kind);
    }
  }

  function cancelPlacementDraft(_reason: 'cancel' | 'toggle' | 'escape' | 'close'): void {
    const draft = placementDraft;
    placementDraft = null;
    previewPointerId = null;
    if (draft?.mode === 'move' && draft.entityId) {
      setEntityVisualHidden(draft.entityId, false);
    }
    clearPlacementVisual();
    if (draft && _reason !== 'toggle' && _reason !== 'close') {
      hud.toast(t('toast.cancelled'));
      if (draft.mode === 'move' && draft.entityId) {
        showEntityPanel(draft.entityId);
      } else {
        clearIdleSelectionRing();
      }
    }
    syncBoostChrome();
    syncCollectCues();
  }

  function confirmPlacementDraft(): boolean {
    if (!placementDraft || !latestPlacementPreview?.valid) {
      return false;
    }
    if (placementDraft.mode === 'move' && placementDraft.entityId) {
      const movedEntityId = placementDraft.entityId;
      const result = session.move(
        movedEntityId,
        latestPlacementPreview.candidateAnchor,
        latestPlacementPreview.candidateRotation,
      );
      if (!result.ok) {
        hud.toast(failMessage(result.reason));
        return false;
      }
      setEntityVisualHidden(movedEntityId, false);
      placementDraft = null;
      latestPlacementPreview = null;
      session.selectedSlotId = null;
      inspectedEntityId = null;
      hud.hidePanel();
      refreshScene(interactionSceneSync({ type: 'worldMutation' }));
      hud.toast(t('toast.moved'));
      return true;
    }
    if (placementDraft.mode === 'build') {
      const slotId = slotByIdForAnchor(latestPlacementPreview.candidateAnchor);
      tryPlace(slotId, placementDraft.kind, {
        anchor: latestPlacementPreview.candidateAnchor,
        rotation: latestPlacementPreview.candidateRotation,
      });
      return !placementDraft;
    }
    return false;
  }

  function showEntityPanel(entityId: string): void {
    const entity = session.state.entities.find((item) => item.id === entityId);
    if (!entity) {
      inspectedEntityId = null;
      hud.hidePanel();
      return;
    }
    inspectedEntityId = entityId;
    const palettes = availablePalettes(entity);
    hud.showEntity(
      entity,
      session.state,
      {
        onCollect: () => {
          tryCollect(entity);
          showEntityPanel(entityId);
        },
        onUpgrade: () => tryUpgrade(entityId),
        palettes,
        selectedPaletteId: resolveApprovedPaletteId(entity.kind, entity.appearance?.paletteId) ?? null,
        onPalette:
          palettes.length > 0
            ? (paletteId) => {
                const result = session.setPalette(entity.id, paletteId);
                if (!result.ok) {
                  hud.toast(failMessage(result.reason));
                }
                refreshScene(false);
                showEntityPanel(entityId);
              }
            : undefined,
        onRequestRemove: () => {
          pendingRemove = { entityId: entity.id, slotId: entity.slotId };
          showEntityPanel(entityId);
        },
        pendingRemove: pendingRemove?.entityId === entity.id,
        onConfirmRemove: () => {
          if (!pendingRemove) {
            return;
          }
          const result = session.remove(pendingRemove.entityId);
          pendingRemove = null;
          playSuccessfulActionCue(result.ok, 'remove');
          if (!result.ok) {
            hud.toast(failMessage(result.reason));
            showEntityPanel(entityId);
            return;
          }
          clearSelectedSlotForRebuild(session);
          inspectedEntityId = null;
          hud.hidePanel();
          hud.toast(t('toast.removed', { n: result.refund ?? 0 }));
          refreshScene(interactionSceneSync({ type: 'worldMutation' }));
        },
        onCancelRemove: () => {
          pendingRemove = null;
          showEntityPanel(entityId);
        },
        removeRefund: purchaseCost(entity.kind),
        onMove: () => {
          beginMoveDraft(entity);
        },
        onRotate: () => {
          if (placementDraft?.mode === 'move' && placementDraft.entityId === entity.id) {
            rotatePlacementDraft();
            return;
          }
          const result = session.rotate(entity.id);
          if (!result.ok) {
            hud.toast(failMessage(result.reason));
            showEntityPanel(entityId);
            return;
          }
          hud.toast(t('toast.rotated'));
          refreshScene(interactionSceneSync({ type: 'worldMutation' }));
          showEntityPanel(entityId);
        },
        relocating: placementDraft?.mode === 'move' && placementDraft.entityId === entity.id,
        onCancelMove: () => {
          cancelPlacementDraft('cancel');
        },
      },
    );
    bindPanelToActiveTarget();
  }

  function tryPlace(
    slotId: string,
    kind: EntityKind,
    options?: { anchor?: SubcellAnchor; rotation?: RotationQuarter },
  ): void {
    const before = resolveOnboardingStep(session.state, session.onboarding);
    const result = session.place(slotId, kind, options);
    playSuccessfulActionCue(result.ok, 'build');
    if (!result.ok) {
      hud.toast(failMessage(result.reason));
      return;
    }
    placementDraft = null;
    latestPlacementPreview = null;
    session.selectedSlotId = null;
    inspectedEntityId = null;
    hud.hidePanel();
    const afterStep = resolveOnboardingStep(session.state, session.onboarding);
    if (before !== afterStep && session.buildMode) {
      session.buildMode = false;
      hud.setBuildMode(false);
    }
    refreshScene(interactionSceneSync({ type: 'worldMutation' }));
    const reward = session.consumeFirstRewardNotice();
    if (before !== 'complete' && afterStep === 'complete') {
      hud.toast(reward > 0 ? t('guide.completeReward', { n: reward }) : t('guide.complete'));
      return;
    }
    hud.toast(t('toast.built', { name: entityName(kind) }));
  }

  function tryCollect(entity: PlacedEntity): void {
    const result = session.collect(entity.id);
    playSuccessfulActionCue(result.ok, 'collect');
    if (!result.ok) {
      hud.toast(failMessage(result.reason));
      return;
    }
    refreshHud();
  }

  function tryCollectAll(): void {
    const amount = session.collectAll();
    playSuccessfulActionCue(amount > 0, 'collect');
    refreshHud();
    if (inspectedEntityId) {
      const openEntity = session.state.entities.find((item) => item.id === inspectedEntityId);
      if (openEntity) {
        hud.refreshEntityIncome(openEntity);
      }
    }
  }

  function tryGuidedConfirm(): void {
    const plan = guidedConfirmPlan(session.state);
    if (!plan.ok) {
      refreshHud();
      return;
    }
    tryPlace(plan.slotId, plan.kind, { anchor: plan.anchor, rotation: 0 });
  }

  function tryUpgrade(entityId: string): void {
    const result = session.upgrade(entityId);
    playSuccessfulActionCue(result.ok, 'upgrade');
    if (!result.ok) {
      hud.toast(failMessage(result.reason));
      showEntityPanel(entityId);
      return;
    }
    hud.toast(t('toast.upgraded'));
    refreshScene(interactionSceneSync({ type: 'worldMutation' }));
    showEntityPanel(entityId);
  }

  function buyExpansion(parcelId: string): void {
    const result = session.expand(parcelId);
    playSuccessfulActionCue(result.ok, 'unlock');
      if (!result.ok) {
        hud.toast(failMessage(result.reason));
        openLandPurchase(parcelId);
        return;
      }
    inspectedEntityId = null;
    selectedLandId = null;
    hud.hidePanel();
    hud.toast(t('toast.landUnlocked'));
    session.selectedSlotId = null;
    refreshScene(true);
  }

  function onSlot(slotId: string): void {
    session.selectedSlotId = slotId;
    pendingRemove = null;
    if (pendingBuildAnchor) {
      openBuildCatalog(slotId, true);
      return;
    }
    const opened = resolveProductionSlotOpen(session.state, slotId);
    if (opened.kind === 'unknown') {
      return;
    }
    if (opened.kind === 'expand') {
      const intent = resolveLockedGroundIntent({
        landMode,
        frontierPlotIds: currentFrontierIds(),
        landId: opened.parcelId,
        unlockedLandIds: session.state.unlockedLandIds,
      });
      if (intent.kind === 'toast-use-land') {
        hud.toast(t('toast.useLand'));
        return;
      }
      if (intent.kind === 'ignore') {
        return;
      }
      openLandPurchase(intent.landId);
      return;
    }
    if (opened.kind === 'entity') {
      showEntityPanel(opened.entity.id);
      return;
    }
    inspectedEntityId = null;
    openBuildCatalog(slotId, Boolean(pendingBuildAnchor));
  }

  function applyFloorBrush(event: PointerEvent): FloorBrushPreview | null {
    if (!host || !floorMode) {
      return null;
    }
    const coord = pickSubcellFromPointer(host.camera, event, host.canvas, host.parkTop);
    if (!coord) {
      return null;
    }
    const brush = evaluateFloorBrushPreview(session.state, coord, hud.floorStyle());
    latestFloorBrush = brush;
    if (previewRoot) {
      syncInteractionPreview(previewRoot, brush, host.parkTop);
    }
    return brush;
  }

  function tryPaintFloor(event: PointerEvent, announceLocked: boolean): boolean {
    if (!host) {
      return false;
    }
    const brush = applyFloorBrush(event);
    if (!brush) {
      return false;
    }
    const key = `${brush.coord.sx},${brush.coord.sz}`;
    if (key === lastPaintKey) {
      return true;
    }
    if (!brush.valid) {
      if (announceLocked) {
        hud.toast(t('toast.useLand'));
      }
      return false;
    }
    const result = session.paintFloor(brush.coord, hud.floorStyle());
    if (!result.ok) {
      if (announceLocked) {
        hud.toast(failMessage(result.reason));
      }
      return false;
    }
    lastPaintKey = key;
    if (floorRebuildRaf) {
      return true;
    }
    floorRebuildRaf = requestAnimationFrame(() => {
      floorRebuildRaf = 0;
      refreshScene(interactionSceneSync({ type: 'floorPaint' }));
    });
    return true;
  }

  function endFloorPaint(pointerId: number): void {
    if (paintPointerId !== pointerId) {
      return;
    }
    paintPointerId = null;
    lastPaintKey = null;
    if (host) {
      try {
        host.canvas.releasePointerCapture(pointerId);
      } catch {
        // Capture may already be released.
      }
    }
  }

  refreshHud();
  hud.setBuildMode(session.buildMode);
  hud.setFloorMode(false);
  hud.setLandMode(false);
  if (session.offlineGranted > 0) {
    if (canShowRewardedUi() && !session.offlineOfferClosed) {
      openWelcomeBackPanel(session.offlineGranted);
    } else {
      hud.toast(t('toast.welcomeBack', { n: session.offlineGranted }));
      if (!session.offlineOfferClosed) {
        pendingOfflineOffer = true;
        welcomeBackAmount = session.offlineGranted;
        const waitMs = Math.max(0, FIRST_SESSION_DELAY_MS - (nowMs() - session.startedAt));
        window.setTimeout(() => {
          maybeSurfacePendingOfflineOffer();
        }, waitMs);
      }
    }
  }
  syncBoostChrome();

  window.addEventListener('beforeunload', () => {
    runtime.session?.persist();
  });
  window.addEventListener('resize', () => {
    syncCollectCues();
  });

  try {
    host = createMigrationSceneHost(mount, renderModel());
    host.canvas.style.touchAction = 'none';
    sessionWatch.attachCanvas(host.canvas);
    previewRoot = createInteractionPreviewRoot();
    host.scene.add(previewRoot);

    function abortTransientSceneGesture(): void {
      if (paintPointerId !== null) {
        endFloorPaint(paintPointerId);
      }
      if (previewPointerId !== null) {
        try {
          host!.canvas.releasePointerCapture(previewPointerId);
        } catch {
          // Capture may already be released.
        }
        previewPointerId = null;
      }
      endCameraPan();
    }

    function applyLiveZoom(nextFactor: number): void {
      host!.setZoomFactor(nextFactor);
      syncCollectCues();
    }

    function idleCameraPanMode(): boolean {
      return isIdleCameraPanMode({
        buildMode: session.buildMode,
        floorMode,
        landMode,
        placementActive: placementDraft != null,
      });
    }

    function oneFingerCameraPanAllowed(): boolean {
      return isOneFingerCameraPanAllowed({
        buildMode: session.buildMode,
        floorMode,
        landMode,
        placementActive: placementDraft != null,
      });
    }

    function applyCameraPanFromClients(
      fromClientX: number,
      fromClientY: number,
      toClientX: number,
      toClientY: number,
    ): void {
      const delta = worldPanFromClientDelta(
        host!.camera,
        host!.canvas,
        host!.parkTop,
        fromClientX,
        fromClientY,
        toClientX,
        toClientY,
      );
      if (!delta) {
        return;
      }
      host!.panBy(delta);
      syncCollectCues();
    }

    let cameraPanPointerId: number | null = null;
    let cameraPanLastX = 0;
    let cameraPanLastY = 0;

    function startCameraPan(event: PointerEvent, fromX = event.clientX, fromY = event.clientY): void {
      worldInteractionActive = true;
      syncBoostChrome();
      cameraPanPointerId = event.pointerId;
      cameraPanLastX = fromX;
      cameraPanLastY = fromY;
      try {
        host!.canvas.setPointerCapture(event.pointerId);
      } catch {
        // Capture is best-effort so drags continue off-canvas.
      }
      if (fromX !== event.clientX || fromY !== event.clientY) {
        applyCameraPanFromClients(fromX, fromY, event.clientX, event.clientY);
        cameraPanLastX = event.clientX;
        cameraPanLastY = event.clientY;
      }
    }

    function moveCameraPan(event: PointerEvent): boolean {
      if (cameraPanPointerId !== event.pointerId) {
        return false;
      }
      applyCameraPanFromClients(cameraPanLastX, cameraPanLastY, event.clientX, event.clientY);
      cameraPanLastX = event.clientX;
      cameraPanLastY = event.clientY;
      return true;
    }

    function endCameraPan(pointerId?: number): void {
      if (pointerId !== undefined && cameraPanPointerId !== pointerId) {
        return;
      }
      if (cameraPanPointerId !== null) {
        try {
          host!.canvas.releasePointerCapture(cameraPanPointerId);
        } catch {
          // Capture may already be released.
        }
      }
      cameraPanPointerId = null;
      worldInteractionActive = false;
      syncBoostChrome();
    }

    let pendingTouchEvent: PointerEvent | null = null;
    let touchIntentTimer: ReturnType<typeof setTimeout> | null = null;

    function snapshotScenePointer(event: PointerEvent): PointerEvent {
      return {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        clientX: event.clientX,
        clientY: event.clientY,
        button: event.button,
        buttons: event.buttons,
        isPrimary: event.isPrimary,
        preventDefault: () => event.preventDefault(),
      } as PointerEvent;
    }

    function clearTouchIntentTimer(): void {
      if (touchIntentTimer !== null) {
        clearTimeout(touchIntentTimer);
        touchIntentTimer = null;
      }
    }

    function discardPendingTouchIntent(): void {
      cancelTouchIntent(touchIntent);
      pendingTouchEvent = null;
      clearTouchIntentTimer();
    }

    function applyScenePointerDown(event: PointerEvent): void {
      const now = performance.now();
      if (!hud.allowsScenePointer(Date.now())) {
        return;
      }
      if (!shouldAcceptScenePointer(event, pointerGate, now)) {
        return;
      }
      if (placementDraft) {
        event.preventDefault();
        rememberAcceptedPointer(pointerGate, event, now);
        hud.suppressTransientPointer();
        previewPointerId = event.pointerId;
        try {
          host!.canvas.setPointerCapture(event.pointerId);
        } catch {
          // Pointer capture is best-effort for drag placement.
        }
        onPreviewPointerMove(event);
        return;
      }
      if (floorMode) {
        event.preventDefault();
        rememberAcceptedPointer(pointerGate, event, now);
        hud.suppressTransientPointer();
        paintPointerId = event.pointerId;
        lastPaintKey = null;
        try {
          host!.canvas.setPointerCapture(event.pointerId);
        } catch {
          // Pointer capture is best-effort for drag paint.
        }
        tryPaintFloor(event, true);
        return;
      }
      const hit = pickSceneHitFromPointer(host!.camera, event, host!.canvas, host!.model, host!.parkTop, host!.scene);
      const resolved = hit ? resolveProductionSceneHit(session.state, hit) : null;
      if (resolved?.kind === 'entity' && !landMode) {
        event.preventDefault();
        rememberAcceptedPointer(pointerGate, event, now);
        hud.suppressTransientPointer();
        session.selectedSlotId = resolved.entity.slotId;
        pendingRemove = null;
        pendingBuildAnchor = null;
        showEntityPanel(resolved.entity.id);
        refreshScene(false);
        return;
      }
      if (landMode) {
        const world = pickWorldOnParkPlane(host!.camera, event, host!.canvas, host!.parkTop);
        if (!world) {
          return;
        }
        const intent = resolveLandModePurchaseFromWorld({
          landMode: true,
          x: world.x,
          z: world.z,
          frontierPlotIds: currentFrontierIds(),
          unlockedLandIds: session.state.unlockedLandIds,
        });
        if (intent.kind !== 'buy') {
          return;
        }
        event.preventDefault();
        rememberAcceptedPointer(pointerGate, event, now);
        hud.suppressTransientPointer();
        pendingBuildAnchor = null;
        openLandPurchase(intent.landId);
        return;
      }
      const coord = hit?.anchor ?? pickSubcellFromPointer(host!.camera, event, host!.canvas, host!.parkTop);
      const ground = resolveUnlockedGroundBuildSeed(session.state, coord);
      if (ground.kind === 'none') {
        return;
      }
      event.preventDefault();
      rememberAcceptedPointer(pointerGate, event, now);
      hud.suppressTransientPointer();
      if (ground.kind === 'locked') {
        hud.toast(t('toast.useLand'));
        return;
      }
      if (ground.kind === 'expand') {
        pendingBuildAnchor = null;
        const intent = resolveLockedGroundIntent({
          landMode,
          frontierPlotIds: currentFrontierIds(),
          landId: ground.landId,
          unlockedLandIds: session.state.unlockedLandIds,
        });
        if (intent.kind === 'toast-use-land') {
          hud.toast(t('toast.useLand'));
          return;
        }
        if (intent.kind === 'ignore') {
          return;
        }
        openLandPurchase(intent.landId);
        return;
      }
      if (landMode) {
        return;
      }
      pendingBuildAnchor = ground.anchor;
      onSlot(ground.slotId);
      refreshScene(false);
    }

    function flushPendingTouchSceneAction(reason: 'timeout' | 'pointerup', pointerId: number | null, nowMs: number): boolean {
      const event = pendingTouchEvent;
      if (!event || !takeFlushableTouchIntent(touchIntent, pointerId, nowMs, reason)) {
        return false;
      }
      pendingTouchEvent = null;
      clearTouchIntentTimer();
      applyScenePointerDown(event);
      return true;
    }

    function scheduleTouchIntentFlush(): void {
      clearTouchIntentTimer();
      touchIntentTimer = setTimeout(() => {
        touchIntentTimer = null;
        flushPendingTouchSceneAction('timeout', null, performance.now());
      }, PINCH_INTENT_WINDOW_MS);
    }

    host.canvas.addEventListener('pointerdown', (event) => {
      nudgeAmbientResume();
      nudgeMusicResume();
      const now = performance.now();
      const pinchNote = notePinchPointerDown(pinchGesture, event);
      if (pinchNote.activated || isPinchSuppressingSceneAction(pinchGesture, now)) {
        event.preventDefault();
        cancelTouchIntentForPinch(touchIntent, pinchNote.activated || hasPendingTouchIntent(touchIntent));
        pendingTouchEvent = null;
        clearTouchIntentTimer();
        abortTransientSceneGesture();
        if (pinchNote.activated || isPinchActive(pinchGesture)) {
          worldInteractionActive = true;
          syncBoostChrome();
        }
        rememberAcceptedPointer(pointerGate, { pointerType: 'touch', pointerId: event.pointerId }, now);
        return;
      }
      if (isDesktopCameraPanPointer(event) && (event.button === 1 || idleCameraPanMode())) {
        event.preventDefault();
        startCameraPan(event);
        return;
      }
      if (isTouchScenePointer(event) && beginTouchIntent(touchIntent, event, now)) {
        event.preventDefault();
        pendingTouchEvent = snapshotScenePointer(event);
        if (!oneFingerCameraPanAllowed()) {
          scheduleTouchIntentFlush();
        }
        return;
      }
      if (idleCameraPanMode() && beginIdleMouseClickIntent(touchIntent, event, now)) {
        event.preventDefault();
        pendingTouchEvent = snapshotScenePointer(event);
        return;
      }
      applyScenePointerDown(event);
    });
    host.canvas.addEventListener('pointermove', (event) => {
      const pinchMove = notePinchPointerMove(pinchGesture, event);
      if (pinchMove) {
        if (!worldInteractionActive) {
          worldInteractionActive = true;
          syncBoostChrome();
        }
        applyLiveZoom(host!.zoomFactor() * pinchMove.distanceScale);
        applyCameraPanFromClients(
          pinchMove.previousCentroid.x,
          pinchMove.previousCentroid.y,
          pinchMove.nextCentroid.x,
          pinchMove.nextCentroid.y,
        );
        return;
      }
      if (isPinchSuppressingSceneAction(pinchGesture, performance.now())) {
        return;
      }
      if (moveCameraPan(event)) {
        return;
      }
      if (hasPendingTouchIntent(touchIntent) && pendingTouchEvent?.pointerId === event.pointerId) {
        pendingTouchEvent = snapshotScenePointer(event);
        if (shouldStartIdleOneFingerPan(touchIntent, event.clientX, event.clientY, oneFingerCameraPanAllowed())) {
          const fromX = touchIntent.startX;
          const fromY = touchIntent.startY;
          discardPendingTouchIntent();
          startCameraPan(event, fromX, fromY);
        }
        return;
      }
      if (placementDraft) {
        onPreviewPointerMove(event);
        return;
      }
      if (floorMode) {
        applyFloorBrush(event);
        if (paintPointerId !== event.pointerId) {
          return;
        }
        if (event.pointerType === 'mouse' && (event.buttons & 1) === 0) {
          return;
        }
        tryPaintFloor(event, false);
      }
    });
    host.canvas.addEventListener('pointerup', (event) => {
      const now = performance.now();
      const pinchUp = notePinchPointerUp(pinchGesture, event, now);
      if (pinchUp.deactivated || isPinchSuppressingSceneAction(pinchGesture, now)) {
        discardPendingTouchIntent();
        abortTransientSceneGesture();
        if (pinchUp.deactivated || !isPinchActive(pinchGesture)) {
          worldInteractionActive = cameraPanPointerId !== null;
          syncBoostChrome();
        }
        rememberAcceptedPointer(pointerGate, { pointerType: 'touch', pointerId: event.pointerId }, now);
        return;
      }
      if (cameraPanPointerId === event.pointerId) {
        endCameraPan(event.pointerId);
        discardPendingTouchIntent();
        return;
      }
      const flushedTouch = flushPendingTouchSceneAction('pointerup', event.pointerId, now);
      const commitNow = flushedTouch
        ? Math.max(now, pointerGate.lastAcceptedAtMs + SCENE_POINTER_DEBOUNCE_MS)
        : now;
      if (placementDraft && previewPointerId === event.pointerId) {
        onPreviewPointerMove(event);
        if (latestPlacementPreview?.valid && acceptPlacementCommit(event, pointerGate, commitNow)) {
          confirmPlacementDraft();
        }
        previewPointerId = null;
        try {
          host!.canvas.releasePointerCapture(event.pointerId);
        } catch {
          // Capture may already be released.
        }
        return;
      }
      endFloorPaint(event.pointerId);
    });
    host.canvas.addEventListener('pointercancel', (event) => {
      const now = performance.now();
      const pinchUp = notePinchPointerUp(pinchGesture, event, now);
      if (pinchUp.deactivated || isPinchSuppressingSceneAction(pinchGesture, now)) {
        discardPendingTouchIntent();
        abortTransientSceneGesture();
        if (pinchUp.deactivated || !isPinchActive(pinchGesture)) {
          worldInteractionActive = cameraPanPointerId !== null;
          syncBoostChrome();
        }
        rememberAcceptedPointer(pointerGate, { pointerType: 'touch', pointerId: event.pointerId }, now);
        return;
      }
      if (hasPendingTouchIntent(touchIntent) && touchIntent.pointerId === event.pointerId) {
        discardPendingTouchIntent();
      }
      if (cameraPanPointerId === event.pointerId) {
        endCameraPan(event.pointerId);
      }
      if (previewPointerId === event.pointerId) {
        previewPointerId = null;
      }
      endFloorPaint(event.pointerId);
    });
    host.canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        applyLiveZoom(host!.zoomFactor() + zoomDeltaFromWheel(event.deltaY, event.deltaMode));
      },
      { passive: false },
    );
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        cancelPlacementDraft('escape');
      }
      const rotate = parkViewRotateDeltaFromKey(event.key, {
        meta: event.metaKey,
        ctrl: event.ctrlKey,
        alt: event.altKey,
      });
      if (rotate && host) {
        host.rotateView(rotate);
        syncCollectCues();
      }
      const zoom = parkViewZoomDeltaFromKey(event.key, {
        meta: event.metaKey,
        ctrl: event.ctrlKey,
        alt: event.altKey,
      });
      if (zoom && host) {
        host.zoomBy(zoom);
        syncCollectCues();
      }
    });
    host.startLoop((visualDeltaMs, incomeDeltaMs = visualDeltaMs) => {
      analytics.updateMilestones();
      syncCollectCues();
      tickAcc += incomeDeltaMs;
      while (tickAcc >= INCOME_TICK_MS) {
        tickAcc -= INCOME_TICK_MS;
        session.tickIncome(1);
        refreshHud();
        if (inspectedEntityId) {
          const openEntity = session.state.entities.find((item) => item.id === inspectedEntityId);
          if (openEntity) {
            hud.refreshEntityIncome(openEntity);
          }
        }
      }
      saveAcc += visualDeltaMs;
      if (saveAcc >= AUTOSAVE_INTERVAL_MS) {
        saveAcc = 0;
        session.persist();
      }
      const attractionCount = session.state.entities.filter(
        (entity) => catalogItem(entity.kind).role === 'attraction',
      ).length;
      maybePlayAttractionAccent(performance.now(), attractionCount);
    });
    webglLoopStarted = true;
    host.renderer.info.reset();
    host.renderOnce();
    const info = host.renderer.info.render;
    console.info('[WU-018] threejs production ready', {
      drawCalls: info.calls,
      triangles: info.triangles,
      storageKey: SAVE_STORAGE_KEY,
    });
  } catch (error) {
    console.warn('[WU-018] WebGL scene unavailable; HUD and canonical save remain active', error);
  }
  if (!webglLoopStarted) {
    window.setInterval(() => {
      analytics.updateMilestones();
    }, 1_000);
  }
  sessionWatch.markReady();
  if (status.usable) {
    emitGameplayStart();
  }
}
